import { z } from 'zod';
import type { RuntimeV3RevisionRef } from './dataset-revision';
import type { RuntimeV3DefinitionVersionRef } from './definitions';

const text = z.string().min(1);
const digest = z.string().regex(/^[a-f0-9]{64}$/i);
const day = z.string().date();
export const REVENUE_ADDRESS_BYTE_LIMIT = 16384;
export const REVENUE_SAVED_TARGET_BYTE_LIMIT = 4096;
export const revenueTransportBytes = (value: unknown): number => new TextEncoder().encode(JSON.stringify(value)).length;
const pin = z.object({ tenantId: text, datasetRef: text, revisionId: text,
  version: z.number().int().positive(), digest, rowContentDigest: digest.optional() }).strict() satisfies z.ZodType<RuntimeV3RevisionRef>;
const definition = z.object({ definitionRef: text, scopeKind: z.enum(['tenant', 'shared']),
  tenantId: text.nullable(), digest: digest.optional(), subset: z.object({ collection: text,
    keys: z.array(text).min(1), digest }).strict().optional() }).strict().refine(value =>
  (value.scopeKind === 'shared') === (value.tenantId === null)
  && (value.digest === undefined) !== (value.subset === undefined)) satisfies z.ZodType<RuntimeV3DefinitionVersionRef>;
export const revenueIdentitySchema = z.object({ companyRef: text, strategyId: text, goalGraphNodeRef: text }).strict();
export const revenueSelectorSchema = z.object({ planVersion: text, actualThrough: day,
  outlookVintage: text, previousOutlookVintage: text, previousActualThrough: day,
  quarterStart: day, quarterEnd: day }).strict();
export const revenueCutDescriptorSchema = z.object({ kind: z.literal('revenue_goal'), id: digest,
  scope: revenueIdentitySchema.extend({ kind: z.enum(['page', 'replay']), tenantId: text, goalVersionRef: text }).strict(),
  selector: revenueSelectorSchema,
  sources: z.object({ inventory: z.array(pin), selected: z.array(z.object({
    kind: z.literal('auxiliary'), slot: text, pin }).strict()) }).strict(),
  definitions: z.array(definition), dates: z.object({ asOf: day, selections: z.array(z.never()) }).strict(),
  ledger: z.array(z.never()),
}).strict().refine(value => value.dates.asOf === value.selector.actualThrough
  && revenueTransportBytes(value) <= REVENUE_ADDRESS_BYTE_LIMIT);
export type RevenueFigureCutDescriptor = z.infer<typeof revenueCutDescriptorSchema>;
/** One view per revenue story beat: on track, why behind, what to do, did it work. */
export const REVENUE_VIEW_IDS = ['plan', 'behind', 'act', 'result'] as const;
export const revenueViewIdSchema = z.enum(REVENUE_VIEW_IDS);
export type RevenueViewId = z.infer<typeof revenueViewIdSchema>;
export const revenueAddressSchema = z.object({ kind: z.literal('revenue_goal'), goalVersionRef: text,
  asOf: day, viewId: revenueViewIdSchema, params: z.object({}).strict(),
  figureCut: revenueCutDescriptorSchema, assertedScope: revenueIdentitySchema.optional(),
}).strict().refine(value => value.goalVersionRef === value.figureCut.scope.goalVersionRef
  && value.asOf === value.figureCut.dates.asOf && revenueTransportBytes(value) <= REVENUE_ADDRESS_BYTE_LIMIT && (!value.assertedScope ||
    value.assertedScope.companyRef === value.figureCut.scope.companyRef
    && value.assertedScope.strategyId === value.figureCut.scope.strategyId
    && value.assertedScope.goalGraphNodeRef === value.figureCut.scope.goalGraphNodeRef));
export type RevenueViewAddress = z.infer<typeof revenueAddressSchema>;
export const revenueSubjectSchema = z.object({ kind: z.literal('revenue_goal'), ref: text,
  companyRef: text, strategyId: text, scopeRef: text }).strict();
export type RevenueInvestigationSubject = z.infer<typeof revenueSubjectSchema>;
export const revenueViewRequestSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('revenue_acquire'), goalVersionRef: text,
    viewId: revenueViewIdSchema }).strict(),
  z.object({ kind: z.literal('revenue_exact'), address: revenueAddressSchema }).strict(),
]);
export type RevenueViewRequest = z.infer<typeof revenueViewRequestSchema>;
export const revenueSavedReadAddressSchema = z.object({ kind: z.literal('revenue_goal'), goalVersionRef: text,
  asOf: day, viewId: revenueViewIdSchema, params: z.object({}).strict(), cutId: digest }).strict();
export type RevenueSavedReadAddress = z.infer<typeof revenueSavedReadAddressSchema>;
export function revenueSavedReadAddress(address: RevenueViewAddress): RevenueSavedReadAddress {
  const { figureCut, assertedScope: _scope, ...compact } = address;
  return { ...compact, cutId: figureCut.id };
}

export function revenueSubjectMatchesAddress(subject: RevenueInvestigationSubject,
  address: RevenueViewAddress): boolean {
  const scope = address.figureCut.scope;
  return subject.ref === address.goalVersionRef && subject.companyRef === scope.companyRef
    && subject.strategyId === scope.strategyId && subject.scopeRef === scope.goalGraphNodeRef;
}
